import { secondsToMilliseconds } from 'date-fns';
import type { TFunction } from 'i18next';

import { RELOGIN_SUCCESS_PATH } from '../nav/config';
import { toast } from '../toasts';
import { SessionTimeoutToast } from './SessionTimeoutToast';
import { LoginWithRedirectCallbackOptions } from './redirect/useLoginWithRedirectCallback';
import { JwtPayload, LoginRequiredErrors } from './types';

export function getLoginOptions(isInSession: boolean): Pick<LoginWithRedirectCallbackOptions, 'returnTo' | 'openUrl'> {
  const returnUrlQueryParams = new URLSearchParams({
    isInSession: isInSession ? 'true' : 'false',
  });

  return {
    returnTo: `${RELOGIN_SUCCESS_PATH}?${returnUrlQueryParams.toString()}`,
    openUrl(url) {
      window.open(url, '_blank');
    },
  };
}

export function loginRequired(authErrorMsg: string) {
  return Object.values(LoginRequiredErrors).some(
    (invalidRefreshTokenErrorMsg) => authErrorMsg === invalidRefreshTokenErrorMsg,
  );
}

export function parseJwt<T = Record<string, unknown>>(jwt: string): (JwtPayload & T) | null {
  try {
    return JSON.parse(atob(jwt.split('.')[1]));
  } catch (e) {
    return null;
  }
}

export function expiredJwt(jwt: string, comparatorTimestamp?: number) {
  const { exp: jwtExp } = parseJwt(jwt) ?? {};
  if (!jwtExp || (comparatorTimestamp ?? Date.now()) < secondsToMilliseconds(jwtExp)) return false;
  return true;
}

const SESSION_TIMEOUT_TOAST_KEY = 'auth-session-expired';

export function showSessionTimeoutToast() {
  toast.enqueue((ariaProps) => <SessionTimeoutToast {...ariaProps} />, {
    timeout: Infinity,
    key: SESSION_TIMEOUT_TOAST_KEY,
  });
}

export function hideShownSessionTimeoutToast() {
  toast.close(SESSION_TIMEOUT_TOAST_KEY);
}

export function showSessionRestoredToast(t: TFunction) {
  toast.success(t('telehealth-core:infoCore.yourLoginSessionHasBeenRestored'));
}
